import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AlertTriangle, CheckCircle2, FolderOpen, Loader2, RefreshCw, Upload } from "lucide-react";
import {
  SKILL_ENTRY_FILE,
  contentTypeFromPath,
  readSkillPackage,
  writeDeclarations,
  type SkillPackage,
} from "@prodgy/skills";

import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { InputWithMagicFill } from "@/shared/components/form/InputWithMagicFill";
import { useMagicFill } from "@/shared/contexts/MagicFillContext";
import { useSuperAdminOrg } from "@/shared/contexts/SuperAdminOrgContext";
import { useFacets } from "@/features/Facets/hooks/useFacets";
import { SkillFacetsField } from "./SkillFacetsField";
import type { FacetFilter } from "@/features/Facets/types";
import { getSkillRegisterFormMetadata } from "@/shared/utils/magicFillMetadataHelpers";
import { DIMENSAO_DE_FERRAMENTA, VOCABULARIO_DE_SKILL } from "../utils/skillFacetVocabulary";
import type { AvailableFacetForMagicFill } from "@/shared/types/magicFillTypes";
import { requestMagicFill } from "@/shared/components/form/magicFillRequest";
import { TextareaWithMagicFill } from "@/shared/components/form/TextareaWithMagicFill";
import { Label } from "@/shared/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Switch } from "@/shared/components/ui/switch";
import { Textarea } from "@/shared/components/ui/textarea";
import { cn } from "@/infrastructure/lib/utils";
import { logger } from "@/infrastructure/lib/logger";
import { useToast } from "@/shared/hooks/use-toast";
import { FilesStep } from "@/features/Skills/components/SkillInstaller/steps";
import { SkillInstallerProgress } from "@/features/Skills/components/SkillInstaller/SkillInstallerProgress";
import { textoDaSkill } from "@/features/Skills/utils/text";
import { invalidarSkills } from "@/features/Skills/utils/invalidate";
import { useAuth } from "@/features/Auth/state/AuthContext";
import { useAvatar } from "@/features/Profile/state/AvatarContext";
import { ProfileService } from "@/features/Profile/services/profileService";
import type { SkillCatalogDetail, SkillInstallationScope } from "@/features/Skills/types/skill";
import { SkillsAdminService, type SkillAdminEntry } from "../services/skillsAdminService";
import type { SkillPublisherScope } from "@/features/Skills/types/skill";
import { IntegrationsDeclarationStep, ParametersDeclarationStep } from "./SkillDeclarationSteps";
import {
  paraDeclaracoes,
  paraOArquivo,
  paraTraducoes,
  parametrosDoPacote,
  requisitosDoPacote,
  type ParametroEmEdicao,
  type RequisitoEmEdicao,
} from "../utils/skillDeclarations";
import {
  lerPasta,
  lerSelecao,
  lerZip,
  PacoteInvalido,
  type PacoteLido,
} from "../utils/skillPackage";

interface SkillPublishDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * A skill de que esta é uma versão NOVA.
   *
   * Ausente, é uma skill nova e a tela abre vazia. Presente, ela abre com o
   * pacote e o cadastro da versão que está no catálogo — publicar a correção de
   * uma frase não deveria exigir ter a pasta original à mão, nem redigitar o
   * que já estava declarado.
   */
  skill?: SkillAdminEntry | null;
  /**
   * Para qual catálogo a skill vai (AB#85637). Ausente, a plataforma.
   *
   * Sai da NAVEGAÇÃO — de qual tela a pessoa clicou em importar —, nunca de um
   * campo do formulário: quem está na própria aba de skills não deveria ter de
   * dizer de novo que a skill é dela.
   */
  destino?: SkillPublisherScope;
}

/**
 * Publicação de skill pela interface (AB#85634, AC2).
 *
 * === Por que os mesmos passos da instalação ===
 *
 * Quem publica e quem instala olham para a mesma coisa, e por isso olham pelas
 * MESMAS telas: `OverviewStep` e `FilesStep` são os componentes
 * do assistente de instalação, alimentados aqui pelo pacote em memória em vez
 * de pela api. Publicar passa a ser conferir o que a outra pessoa vai ver.
 *
 * Isso só é possível porque aqueles passos recebem a função de leitura de quem
 * os usa — o que antes era um `target` fixo. A mesma generalização que serviu à
 * tela de administração serve aqui, lendo do que ainda nem foi enviado.
 *
 * === O que é do pacote e o que é do catálogo ===
 *
 * O `name` vem do `SKILL.md` e é fixo: é a identidade com que o agente pede a
 * skill, e tem de casar com a pasta. `title` e `description` partem do pacote e
 * podem ser ajustados — nem todo pacote declara título, e sem ele o cartão cai
 * no slug. Nível de instalação, etiquetas e changelog não existem no pacote.
 */
/**
 * Os idiomas em que o catálogo pode ser escrito, mais o canônico.
 *
 * `null` é o valor do pacote: o que o agente lê e o que vai para dentro do
 * `SKILL.md`. Os outros são complemento do Prodgy — o que o pacote não tem por
 * que saber, e o que não se traduz de volta para ele.
 */
/**
 * Os idiomas da aplicação, no próprio idioma — que é como se escolhe idioma em
 * qualquer lugar.
 *
 * Não há opção "do pacote": o que o pacote declara não se edita aqui. Ele
 * aparece como referência (no campo vazio, como placeholder) e continua valendo
 * quando ninguém escreve nada.
 */
const IDIOMAS: Array<{ id: string; rotulo: string }> = [
  { id: "pt", rotulo: "Português" },
  { id: "en", rotulo: "English" },
];

/**
 * Quantas etiquetas a sugestão pode deixar no campo.
 *
 * O modelo, solto, devolveu 39 numa skill — cada ferramenta citada no
 * procedimento virou uma. Etiqueta serve para ACHAR a skill na loja, e uma
 * lista desse tamanho não classifica nada: só enche o cartão. Oito é o teto do
 * que uma pessoa lê de relance; o que passar disso quem importa acrescenta à
 * mão, sabendo por quê.
 */
const LIMITE_DE_ETIQUETAS = 8;

/**
 * Palavras que sobram quando o modelo responde em prosa.
 *
 * O campo de ferramentas pede uma lista, mas a resposta vem como
 * "Python (FastMCP) and Node/TypeScript for MCP server development" — e partir
 * isso por espaço produzia `tool:and`, `tool:for`, `tool:(python`. Não é
 * vocabulário: é o que sobra de uma frase picada.
 */
const PALAVRAS_VAZIAS = new Set([
  "a",
  "an",
  "and",
  "as",
  "at",
  "com",
  "de",
  "do",
  "da",
  "e",
  "for",
  "from",
  "in",
  "into",
  "of",
  "on",
  "or",
  "para",
  "the",
  "to",
  "using",
  "via",
  "with",
]);

/**
 * O valor de uma etiqueta, limpo.
 *
 * LIMPA em vez de recusar: `(python` e `development)` chegaram assim porque o
 * modelo escreveu uma frase, e jogar fora o que tem um parêntese perderia
 * `python`, que é a informação. Fica minúsculo, sem acento e com hífen no lugar
 * do resto — a forma que a facet da organização exige e pela qual o filtro casa.
 */
const valorDeEtiqueta = (bruto: string): string =>
  bruto
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9.+_-]+/g, "-")
    .replace(/^[-.]+|[-.]+$/g, "");

/**
 * Põe a sugestão de etiquetas na forma que o cadastro de facets aceita.
 *
 * O modelo devolve o que quiser — lista com vírgula, maiúsculas, acento. A
 * facet da organização é minúscula por restrição do banco e casa por igualdade
 * exata, então normalizar aqui é o que impede a etiqueta sugerida de nunca
 * encontrar nada no filtro. O que não tiver a forma `nome:valor` é descartado.
 */
const normalizaEtiquetas = (bruto: string): string =>
  bruto
    .split(/[\s,]+/)
    .map((etiqueta) => {
      const [name, ...resto] = etiqueta.trim().toLowerCase().split(":");
      const value = valorDeEtiqueta(resto.join(":"));

      return name && value ? `${valorDeEtiqueta(name)}:${value}` : "";
    })
    .filter((etiqueta) => /^[a-z0-9_-]+:[a-z0-9][a-z0-9.+_-]*$/.test(etiqueta))
    .join(" ");

/**
 * As etiquetas como o Magic Fill as devolve — que NÃO é uma string.
 *
 * O serviço responde facets em `[{ name, value }]`, o mesmo formato que a
 * importação da base de conhecimento recebe. Tratar a resposta como texto
 * produzia `[object Object]`, que a normalização descartava inteiro: o campo
 * ficava vazio sem erro nenhum, nem no log nem na tela.
 *
 * As três formas são aceitas porque o modelo alterna entre elas conforme o
 * prompt — e prompt aqui é configuração de banco, não código.
 */
const etiquetasDaSugestao = (valor: unknown): FacetFilter[] => {
  const cru: string[] =
    typeof valor === "string"
      ? valor.split(/[\s,]+/)
      : Array.isArray(valor)
        ? valor.map((item) => {
            if (typeof item === "string") return item;

            const objeto = item as { name?: unknown; value?: unknown };

            return typeof objeto?.name === "string" && typeof objeto?.value === "string"
              ? `${objeto.name}:${objeto.value}`
              : "";
          })
        : [];

  return normalizaEtiquetas(cru.join(" "))
    .split(" ")
    .filter(Boolean)
    .map((etiqueta) => {
      const [name, value] = etiqueta.split(":");

      return { name: name!, value: value! };
    });
};

/**
 * As ferramentas sugeridas viram etiquetas `tool:<nome>`.
 *
 * Elas chegam como texto solto porque não há lista fechada de ferramenta no
 * mundo — e o filtro de vocabulário do serviço descartaria qualquer uma que não
 * estivesse prevista.
 */
const ferramentasDaSugestao = (valor: unknown): FacetFilter[] => {
  const cru = typeof valor === "string" ? valor : Array.isArray(valor) ? valor.join(",") : "";

  const nomes = cru
    // Parêntese, barra e "e"/"and" são pontuação de frase, não nome de
    // ferramenta: "Node/TypeScript (MCP SDK)" são três nomes, e o que separa
    // eles não é só a vírgula.
    .split(/[\s,;/()[\]]+/)
    .map(valorDeEtiqueta)
    // Uma letra não é nome de ferramenta, e palavra de ligação tampouco — foi
    // delas que saíram `tool:for` e `tool:and` na tela.
    .filter((nome) => nome.length > 1 && !PALAVRAS_VAZIAS.has(nome));

  return [...new Set(nomes)].map((nome) => ({ name: DIMENSAO_DE_FERRAMENTA, value: nome }));
};

/** Descarta idioma sem texto: mapa com string vazia diria que alguém traduziu. */
const semVazios = (mapa: Record<string, string>): Record<string, string> =>
  Object.fromEntries(Object.entries(mapa).filter(([, texto]) => texto.trim().length > 0));

/**
 * A troca de idioma dos campos de catálogo.
 *
 * Grupo segmentado à mão, como o da barra de filtros da loja: o produto não tem
 * um toggle group, e trazer um componente novo para três botões custaria mais do
 * que resolve.
 */
const SeletorDeIdioma = ({
  valor,
  onChange,
  vazios,
}: {
  valor: string;
  onChange: (idioma: string) => void;
  /** Idiomas ainda sem texto — ganham um ponto, para a falta não se esconder. */
  vazios?: string[];
}) => (
  <div className="inline-flex h-8 items-center rounded-md border bg-muted/40 p-0.5">
    {IDIOMAS.map((opcao) => (
      <button
        key={opcao.id}
        type="button"
        onClick={() => onChange(opcao.id)}
        className={cn(
          "flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-xs font-medium transition-colors",
          valor === opcao.id
            ? "bg-background text-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground",
        )}
      >
        {opcao.rotulo}
        {vazios?.includes(opcao.id) && (
          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />
        )}
      </button>
    ))}
  </div>
);

export function SkillPublishDialog({
  open,
  onOpenChange,
  skill,
  destino = "platform",
}: SkillPublishDialogProps) {
  /**
   * No espaço pessoal o formulário tem MENOS a decidir, e não outro desenho.
   *
   * Três campos deixam de fazer pergunta ali: o nível de instalação (só existe
   * um — o dela), o "core" (que é a plataforma impondo à casa inteira) e o dono
   * entre superadministradores (a dona é quem está importando). Mantê-los seria
   * pedir decisão onde não há escolha.
   */
  const pessoal = destino === "user";
  const { t, i18n: i18nAtual } = useTranslation();
  const { toast } = useToast();
  const { user } = useAuth();
  const { avatarUrl } = useAvatar();
  const queryClient = useQueryClient();

  const [pacote, setPacote] = useState<PacoteLido | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [lendo, setLendo] = useState(false);
  const [arrastando, setArrastando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [passo, setPasso] = useState(0);

  // Dois seletores porque um `<input>` não faz os dois: `webkitdirectory`
  // escolhe pasta e esconde arquivo solto; sem ele, não se escolhe pasta.
  const seletorPasta = useRef<HTMLInputElement>(null);
  const seletorZip = useRef<HTMLInputElement>(null);

  /**
   * O nível em que a skill se instala — e ele começa VAZIO.
   *
   * Vinha em "workspace" por padrão, e campo já preenchido não se lê: a skill
   * era importada no nível que a tela escolheu, não no que alguém decidiu. Como
   * a declaração é UMA só (AC28) e decide quem enxerga a skill na loja, ela tem
   * de ser escolha — por isso é obrigatória para importar.
   */
  const [escopo, setEscopo] = useState<SkillInstallationScope | "">("");
  /**
   * O nível que vai no pedido.
   *
   * No espaço pessoal não há o que escolher — a skill só cabe onde ela vive —,
   * e por isso o campo não aparece e o valor é o único possível.
   */
  const escopoFinal: SkillInstallationScope | "" = pessoal ? "user" : escopo;
  /**
   * `null` enquanto ninguém editou: vale o valor do pacote. Editado, passa a
   * valer o texto daqui — inclusive vazio, que também é uma escolha.
   */

  /**
   * As etiquetas escolhidas, no mesmo formato da importação da base de
   * conhecimento: pares `{ name, value }` vindos do seletor de facetas, e não
   * texto solto. É o vocabulário da organização que manda — inventar etiqueta
   * fora dele cria facet que nenhum filtro encontra.
   */
  const [etiquetas, setEtiquetas] = useState<FacetFilter[]>([]);
  /**
   * Em que idioma se está escrevendo título, descrição e changelog.
   *
   * `null` é o CANÔNICO — o valor do pacote, que o agente lê e que vai para
   * dentro do `SKILL.md`. Os demais são complemento do catálogo (AC37): o que
   * não for traduzido cai no canônico, nunca em branco.
   */
  const [idioma, setIdioma] = useState<string>(() =>
    (i18nAtual.language ?? "pt").startsWith("en") ? "en" : "pt",
  );
  const [tituloI18n, setTituloI18n] = useState<Record<string, string>>({});
  const [descricaoI18n, setDescricaoI18n] = useState<Record<string, string>>({});
  const [changelogI18n, setChangelogI18n] = useState<Record<string, string>>({});
  const [core, setCore] = useState(false);

  /**
   * Quem acende o spinner DENTRO do campo.
   *
   * É o mesmo canal da varinha manual: o `Input` e o `Textarea` trocam a varinha
   * por um `Loader2` quando o campo está marcado aqui. Sem isso, a espera pela
   * IA parecia campo travado — que foi exatamente como apareceu no teste.
   */
  const { setFieldLoading } = useMagicFill();
  /**
   * O assistente está preparando o cadastro: leu o pacote e espera a sugestão.
   *
   * Existe para a pessoa não cair num formulário vazio e achar que nada
   * aconteceu. Enquanto é verdade, o passo do pacote segura a tela com o motivo
   * escrito, e o avanço só acontece quando há o que conferir.
   */
  const [preparando, setPreparando] = useState(false);

  /**
   * O vocabulário de facetas da organização ativa.
   *
   * `facetsLoaded` é esperado antes de disparar a sugestão pela mesma razão que
   * o import da KB documenta: sem a lista, `availableFacets` vai vazio, o
   * serviço não injeta as instruções de faceta e o que o modelo devolver é
   * descartado na normalização.
   */
  const { selectedOrganization } = useSuperAdminOrg();
  /**
   * De qual organização sai o vocabulário.
   *
   * Na área de superadministração a organização ativa pode estar VAZIA — é o
   * escopo de plataforma. Aí vale a organização da própria pessoa, que é o
   * mesmo padrão do `FacetSelector`; sem isso o seletor abriria sem nada e a
   * sugestão nunca teria vocabulário para citar.
   */
  const orgDasFacetas =
    selectedOrganization?.id ??
    user?.organizationId ??
    user?.user_metadata?.organization_id ??
    null;
  const { data: facetRows = [], isSuccess: facetsLoaded } = useFacets(orgDasFacetas);

  /**
   * Quando é seguro pedir a sugestão.
   *
   * Sem organização nenhuma não há vocabulário a esperar — e travar aqui foi o
   * que impediu o preenchimento automático de acontecer.
   */
  const vocabularioPronto = !orgDasFacetas || facetsLoaded;

  /**
   * O vocabulário que o Magic Fill pode usar.
   *
   * SÓ o das skills — área, categoria, função. O serviço descarta todo par fora
   * do vocabulário recebido, então esta lista é também o limite do que pode
   * voltar: mandar junto o cadastro da organização, que é o dos DOCUMENTOS da
   * base de conhecimento, fez a skill voltar marcada como `format:full`,
   * `status:draft` e `type:training` — verdades sobre um documento, nada sobre
   * a skill.
   *
   * O que a organização já usa continua aparecendo na LISTA do campo (ver
   * `sugestoesDeEtiqueta`), para quem importa seguir a estrutura de quem vai
   * instalar. Uma coisa é o que se oferece a uma pessoa que escolhe; outra é o
   * que se autoriza a IA a inventar.
   */
  const facetsParaIA = VOCABULARIO_DE_SKILL;

  /** O que a LISTA do campo oferece: o vocabulário das skills mais o da casa. */
  const sugestoesDeEtiqueta = useMemo<AvailableFacetForMagicFill[]>(() => {
    const daOrganizacao = facetRows.map((facet) => ({
      name: facet.name,
      value: facet.value,
      label: undefined,
      parent: null,
    }));

    const vistos = new Set<string>();

    return [...VOCABULARIO_DE_SKILL, ...daOrganizacao].filter((facet) => {
      const chave = `${facet.name}:${facet.value}`;
      if (vistos.has(chave)) return false;
      vistos.add(chave);

      return true;
    });
  }, [facetRows]);
  const [requisitos, setRequisitos] = useState<RequisitoEmEdicao[]>([]);
  const [parametros, setParametros] = useState<ParametroEmEdicao[]>([]);

  /**
   * Instalar junto só é possível quando não há nada a perguntar (AB#85637).
   *
   * Instalar cobra duas respostas que só uma pessoa pode dar: **qual** das
   * integrações configuradas atende cada requisito, e o valor de cada
   * parâmetro. A segunda é a mais delicada — o valor respondido entra dentro do
   * texto que o agente lê, então arbitrá-lo mudaria o procedimento em silêncio.
   *
   * Por isso a opção fica bloqueada, e não escondida: escondê-la faria a pessoa
   * procurar por que ela some em algumas skills e não em outras. Bloqueada com
   * o motivo à vista, ela ensina a regra.
   */
  const perguntaAlgo = requisitos.length > 0 || parametros.length > 0;

  /**
   * Se cabe oferecer **Publicar e instalar** como ação (AB#85637).
   *
   * Três condições, e cada uma por um motivo diferente:
   *
   *  - **destino da organização** — no espaço pessoal instalar já é parte do
   *    ato, e no catálogo da plataforma não há para quem instalar;
   *  - **nada a perguntar** — instalar cobra qual integração atende cada
   *    requisito e o valor de cada parâmetro, e ninguém pode responder isso
   *    pela pessoa. O valor de um parâmetro entra dentro do texto que o agente
   *    lê: arbitrá-lo mudaria o procedimento em silêncio;
   *  - **nível de instalação igual ao destino** — uma skill declarada como de
   *    workspace não se instala na organização, e o botão prometeria o que a
   *    api recusaria.
   */
  const cabeInstalarJunto =
    destino === "organization" && !perguntaAlgo && escopoFinal === "organization";

  /**
   * O catálogo, para saber se este nome já está publicado.
   *
   * Mesma chave da tabela de trás, então normalmente já está em cache: publicar
   * é o nome que decide se isto é uma skill nova ou uma versão a mais dela.
   */
  const { data: catalogo } = useQuery({
    queryKey: ["skills_admin_catalog"],
    queryFn: () => SkillsAdminService.list(),
    enabled: open,
  });

  /** O que o leitor entendeu do pacote — as mesmas regras que a api aplica. */
  const leitura = useMemo(() => (pacote ? readSkillPackage(pacote) : null), [pacote]);
  const lido = leitura?.ok ? leitura.skill : null;

  /**
   * Quem ESCREVEU a skill — a declaração do pacote, e nada mais.
   *
   * Antes, sem `metadata.author`, entrava quem estava registrando: uma skill
   * baixada da internet passava a exibir o nome de quem a subiu, como se a
   * tivesse escrito. São duas coisas diferentes, e agora são dois campos: o
   * autor é do pacote e não muda de mão; o DONO (`dono`, logo abaixo) é quem
   * responde por ela aqui dentro, escolhido na apresentação.
   */
  const autor = lido?.metadata.author ?? null;

  /**
   * O dono da skill no Prodgy: quem responde por ela, e pode mudar.
   *
   * A lista é a dos superadministradores — quem cura o catálogo da plataforma —
   * e abre em quem está registrando, que é o caso comum.
   */
  const { data: perfisSuperadmin } = useQuery({
    queryKey: ["profiles", "role", "superadmin", "active"],
    queryFn: () => ProfileService.getByRole("superadmin", "active"),
    enabled: open,
  });

  /**
   * A lista que o campo oferece.
   *
   * O filtro se repete aqui de propósito: a rota já recebe `role` e `status`,
   * mas quem responde por uma skill não pode sair de uma lista que dependa de a
   * query ter sido aplicada — e a tela mostrava perfis que não eram
   * superadministradores. Em ordem alfabética, porque é assim que se procura
   * uma pessoa pelo nome.
   */
  const superadmins = useMemo(
    () =>
      (perfisSuperadmin ?? [])
        .filter((perfil) => perfil.role === "superadmin" && perfil.status === "active")
        .sort((a, b) =>
          (a.full_name || a.email).localeCompare(b.full_name || b.email, undefined, {
            sensitivity: "base",
          }),
        ),
    [perfisSuperadmin],
  );

  const [dono, setDono] = useState<string | null>(null);

  /**
   * De onde o pacote veio.
   *
   * Vazio é legítimo — skill escrita na casa não tem origem externa —, e por
   * isso o campo não entra no que trava o avanço.
   */
  const [origem, setOrigem] = useState("");

  useEffect(() => {
    if (!open) return;
    setDono((atual) => atual ?? skill?.owner_user_id ?? user?.id ?? null);
  }, [open, skill, user]);

  /** A skill que este pacote vai continuar, quando já existir uma com o nome. */
  const jaNoCatalogo = lido ? catalogo?.find((skill) => skill.name === lido.name) : undefined;

  /**
   * O que a vitrine vai mostrar a quem olha AGORA: a tradução do idioma aberto,
   * e o texto do pacote quando não há tradução nenhuma.
   *
   * O canônico não é editável aqui: ele é do pacote (AC37). O que se escreve no
   * assistente é o complemento do catálogo, por idioma.
   */
  const tituloFinal = textoDaSkill(lido?.title, tituloI18n, idioma) || null;
  const descricaoFinal = textoDaSkill(lido?.description, descricaoI18n, idioma);

  /**
   * O pacote lido, na forma que os passos da instalação esperam.
   *
   * Os campos que só existem depois de publicar recebem o valor que terão: a
   * versão é a próxima da skill (a 1 quando o nome é novo), e o dono fica sem
   * rosto porque a api é que resolve o perfil de quem assina.
   */
  const comoDetalhe: SkillCatalogDetail | null = useMemo(() => {
    if (!lido) return null;

    return {
      name: lido.name,
      title: tituloFinal,
      description: descricaoFinal,
      // A prévia mostra o que foi escrito por idioma: quem registra confere a
      // mesma página que quem instala vai ver, no idioma dele.
      title_i18n: semVazios(tituloI18n),
      description_i18n: semVazios(descricaoI18n),
      publisher_scope: destino,
      provider: lido.metadata.provider ?? null,
      owner_profile: null,
      // A prévia precisa de um valor; a escolha de verdade é cobrada na revisão.
      install_scope: escopoFinal || "product",
      compatibility: lido.compatibility,
      license: lido.license,
      author: autor,
      source_url: origem.trim() || null,
      version: (jaNoCatalogo?.version ?? 0) + 1,
      metadata: lido.metadata,
      facets: etiquetas.map((etiqueta) => `${etiqueta.name}:${etiqueta.value}`),
      frontmatter: lido.frontmatter,
      body: lido.body,
      readme_path: lido.readmePath,
      license_path: lido.licensePath,
      resources: lido.resources.map((recurso) => ({
        path: recurso.path,
        content_type: recurso.contentType,
        size_bytes: recurso.sizeBytes,
        binary: recurso.binary,
      })),
    };
  }, [
    lido,
    autor,
    origem,
    jaNoCatalogo,
    tituloFinal,
    descricaoFinal,
    tituloI18n,
    descricaoI18n,
    escopoFinal,
    destino,
    etiquetas,
  ]);

  /**
   * A imagem que o próprio pacote traz, por convenção de nome na raiz.
   *
   * Reconhecida pelo nome, e não declarada: o formato aberto não tem campo de
   * logo, e inventar um tiraria a portabilidade que é o ponto dele. Sem
   * arquivo, fica a inicial — nunca um espaço vazio onde deveria haver a marca.
   */
  const logo = useMemo(() => {
    const arquivo = pacote?.files.find((item) =>
      /^(logo|icon)\.(png|jpe?g|svg|webp)$/i.test(item.path),
    );
    if (!arquivo) return null;

    const tipo = contentTypeFromPath(arquivo.path);

    return arquivo.encoding === "base64"
      ? `data:${tipo};base64,${arquivo.content}`
      : `data:${tipo};utf8,${encodeURIComponent(arquivo.content)}`;
  }, [pacote]);

  /** Lê do pacote em memória: nada foi enviado ainda. */
  const lerArquivo = async (caminho: string) => {
    const arquivo = pacote?.files.find((item) => item.path === caminho);
    if (!arquivo) throw new Error(`no file at "${caminho}"`);

    return {
      content:
        arquivo.encoding === "base64"
          ? new TextDecoder().decode(Uint8Array.from(atob(arquivo.content), (c) => c.charCodeAt(0)))
          : arquivo.content,
    };
  };

  /**
   * De qual pacote o cadastro já foi lido.
   *
   * O formulário abre com o que o pacote declara, e não em branco: o `SKILL.md`
   * de terceiro costuma trazer as exigências dele, e apagá-las silenciosamente
   * ao importar mudaria a skill sem ninguém pedir. A marca existe para isso
   * acontecer UMA vez por pacote, e não a cada tecla digitada depois.
   */
  const preenchidoPara = useRef<string | null>(null);
  /**
   * Para qual pacote a sugestão já foi pedida.
   *
   * Separado do anterior de propósito: o vocabulário de facetas chega DEPOIS do
   * pacote, e com uma marca só a leitura do arquivo consumiria a rodada — a
   * sugestão nunca aconteceria, porque o efeito já teria se dado por feito.
   */
  const sugeridoPara = useRef<string | null>(null);

  /**
   * O cadastro no ponto de partida.
   *
   * Numa versão nova, o ponto de partida é a skill do catálogo; numa
   * importação, é o vazio — e quem preenche é o pacote mais a sugestão da IA.
   *
   * É uma função, e não um efeito, porque acontece em DOIS momentos: ao abrir e
   * ao trocar o pacote. Trocar o arquivo é recomeçar.
   */
  const semearCadastro = useCallback(() => {
    preenchidoPara.current = null;
    sugeridoPara.current = null;

    setEscopo(skill?.install_scope ?? "");
    setCore(skill?.is_core ?? false);
    // O catálogo guarda `nome:valor`; o seletor fala em pares.
    setEtiquetas(
      (skill?.facets ?? [])
        .map((etiqueta) => etiqueta.split(":"))
        .filter((partes) => partes.length === 2)
        .map(([name, value]) => ({ name: name!, value: value! })),
    );
    setTituloI18n(skill?.title_i18n ?? {});
    setDescricaoI18n(skill?.description_i18n ?? {});
    setChangelogI18n({});
    setRequisitos([]);
    setParametros([]);
    setOrigem(skill?.source_url ?? "");
  }, [skill]);

  /**
   * Versão nova: abre com o pacote que está no catálogo.
   *
   * O cadastro — nível, core, etiquetas — vem da skill; título, descrição,
   * integrações e parâmetros saem do próprio pacote, pelo mesmo caminho de
   * sempre, porque desde o AB#85634 eles moram dentro do `SKILL.md`.
   */
  useEffect(() => {
    if (!open || !skill) return;

    semearCadastro();

    let valeu = true;
    setLendo(true);

    SkillsAdminService.packageOf(skill.name)
      .then((baixado) => {
        if (!valeu) return;
        setPacote({ folderName: baixado.folder_name, files: baixado.files });
      })
      .catch((falha: unknown) => {
        if (valeu) setErro(falha instanceof Error ? falha.message : t("skills.publish.readFailed"));
      })
      .finally(() => {
        if (valeu) setLendo(false);
      });

    return () => {
      valeu = false;
    };
  }, [open, skill, semearCadastro, t]);

  useEffect(() => {
    if (!lido) return;

    const primeiraLeitura = preenchidoPara.current !== lido.name;

    if (primeiraLeitura) {
      preenchidoPara.current = lido.name;
      setRequisitos(requisitosDoPacote(lido.frontmatter, lido.requiredIntegrations));
      setParametros(parametrosDoPacote(lido.frontmatter));

      // O palpite de origem: nenhuma destas chaves é do padrão aberto, mas são
      // as que os pacotes usam na prática. Só preenche o que estiver vazio —
      // numa versão nova, a origem gravada no catálogo é que vale.
      const declarada = ["source", "repository", "homepage"]
        .map((chave) => lido.metadata[chave]?.trim())
        .find((valor) => valor);
      if (declarada) setOrigem((atual) => atual || declarada);
    }

    // O título do PACOTE, quando ele declara um, entra nos dois idiomas como
    // ponto de partida. O resto é a varinha ou a digitação — converter o `name`
    // em título aqui era um mecanismo só desta tela, e o produto já tem o dele.
    const base = lido.title?.trim();
    if (primeiraLeitura && base) {
      setTituloI18n((atual) =>
        Object.fromEntries(IDIOMAS.map((opcao) => [opcao.id, atual[opcao.id]?.trim() || base])),
      );
    }

    // E o que faltar sai do Magic Fill, na hora: quem acabou de soltar o pacote
    // não deveria precisar clicar em duas varinhas por idioma para ter a vitrine
    // pronta.
    // `facetsLoaded` entra nas dependências: sem o vocabulário carregado, a
    // sugestão de etiqueta seria descartada na normalização, e o efeito rodaria
    // uma vez só — com o campo vazio para sempre.
    if (vocabularioPronto && sugeridoPara.current !== lido.name) {
      sugeridoPara.current = lido.name;
      void sugerirVitrine(lido, base ?? null);
    }
    // `sugerirVitrine` lê estado que muda a cada tecla; incluí-la nas
    // dependências faria a sugestão disparar de novo a cada digitação. Ela roda
    // uma vez por pacote, que é o que o efeito inteiro promete.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lido, vocabularioPronto]);

  /**
   * Os passos do assistente — e o upload NÃO é um deles.
   *
   * Depois que o pacote foi lido, aquela etapa não decidia mais nada: mostrava o
   * nome da pasta e um botão de trocar, que agora vivem no cabeçalho, ao lado da
   * identidade da skill. Uma parada a menos entre soltar o arquivo e conferir o
   * cadastro.
   */
  const passos = useMemo(() => {
    const lista: Array<{ id: string; label: string }> = [];
    if (!lido) return lista;

    lista.push({ id: "overview", label: t("skills.steps.overview") });
    lista.push({ id: "content", label: t("skills.steps.files") });
    lista.push({ id: "integrations", label: t("skills.steps.integrations") });
    lista.push({ id: "parameters", label: t("skills.steps.parameters") });
    // "Revisão", e não "Catálogo": o passo é a última olhada antes de registrar
    // — nível, core, compatibilidade, etiquetas e changelog juntos.
    lista.push({ id: "catalog", label: t("skills.publish.stepReview") });

    return lista;
  }, [lido, t]);

  /**
   * Enquanto isto é verdade, a tela do upload continua na frente.
   *
   * O formulário só aparece PRONTO: com o pacote lido e a sugestão aplicada. Ver
   * a estrutura montar-se campo a campo, com a régua de passos convidando a
   * avançar por cima do que a IA ainda escreve, era pior do que esperar — e a
   * espera tem um lugar óbvio para acontecer, que é onde o arquivo foi solto.
   */
  /**
   * Pacote recusado não tem o que preparar.
   *
   * Sem isto, a espera acesa na entrega do arquivo ficaria acesa para sempre num
   * pacote inválido — e com o spinner por cima da lista de recusas, que é
   * justamente o que a pessoa precisa ler ali.
   */
  useEffect(() => {
    if (leitura && !leitura.ok) setPreparando(false);
  }, [leitura]);

  const montando = !lido || lendo || preparando;

  const atual = passos[Math.min(passo, passos.length - 1)];
  const ultimo = passo >= passos.length - 1;

  /**
   * O que impede avançar do passo de apresentação.
   *
   * Título e descrição são obrigatórios: sem eles o cartão da loja cai no slug e
   * o agente fica só com o texto do pacote, que pode não ser o que esta casa
   * quer publicar. Basta UM idioma — os demais caem nele, que é a promessa do
   * AC37 —, e o seletor marca o idioma que ainda está sem texto.
   */
  const faltaCadastro =
    atual?.id === "overview" && (!paraOArquivo(tituloI18n) || !paraOArquivo(descricaoI18n));

  const limpar = () => {
    setPacote(null);
    setErro(null);
    setPasso(0);
    setDono(null);
    semearCadastro();
  };

  /**
   * Devolve a área de soltar e DESMONTA o cadastro.
   *
   * Antes, trocar o pacote guardava o que já estava preenchido — a ideia era
   * poupar quem subia um `SKILL.md` corrigido da mesma skill. Só que o botão
   * também serve para importar OUTRA skill, e aí o formulário seguia exibindo o
   * título, a descrição e as etiquetas da anterior: um cadastro que ninguém
   * escreveu para aquele pacote, prestes a ser importado com ele.
   *
   * Então recomeça. Numa versão nova, o ponto de partida continua sendo o
   * cadastro que está no catálogo, que é o que `semearCadastro` devolve.
   */
  const trocarPacote = () => {
    setPacote(null);
    setErro(null);
    setPasso(0);
    semearCadastro();
  };

  const fechar = (aberto: boolean) => {
    if (!aberto) limpar();
    onOpenChange(aberto);
  };

  /**
   * Guarda o pacote e, quando ele é válido, já avança.
   *
   * O passo do pacote acabou no instante em que o arquivo foi lido — parar ali
   * pedindo um "avançar" seria pedir a confirmação de algo que a pessoa acabou
   * de fazer. Pacote recusado fica onde está: a recusa é a informação do passo.
   */
  const aplicar = (lidoDoDisco: PacoteLido) => {
    // Versão nova é versão DESTA skill: um pacote com outro nome criaria outra
    // skill no catálogo, que não é o que a tela prometeu.
    const leituraNova = readSkillPackage(lidoDoDisco);

    if (skill && leituraNova.ok && leituraNova.skill.name !== skill.name) {
      setErro(
        t("skills.publish.otherSkill", { expected: skill.name, got: leituraNova.skill.name }),
      );
      return;
    }

    // A preparação começa AQUI, e não no efeito que pede a sugestão: entre
    // guardar o pacote e o efeito rodar existe um quadro em que já há skill lida
    // e nada mais acontecendo — e o formulário inteiro piscava nesse quadro,
    // antes de a tela voltar para o carregamento. Quem solta o arquivo vê a
    // espera começar, sem o lampejo de um formulário que ainda não está pronto.
    //
    // Quem apaga é `sugerirVitrine`, em qualquer desfecho — inclusive falha — e
    // o efeito abaixo, quando o pacote é recusado e sugestão nenhuma virá.
    setPreparando(true);
    setPacote(lidoDoDisco);
  };

  const receber = async (transfer: DataTransfer) => {
    setLendo(true);
    setErro(null);

    try {
      // Pasta primeiro: quando se arrasta uma, o `File` correspondente é
      // inútil — só o `entry` alcança o conteúdo.
      const entry = transfer.items[0]?.webkitGetAsEntry?.();

      if (entry?.isDirectory) {
        aplicar(await lerPasta(entry as FileSystemDirectoryEntry));
        return;
      }

      const arquivo = transfer.files[0];
      if (!arquivo) throw new PacoteInvalido(t("skills.publish.dropNothing"));

      if (!arquivo.name.toLowerCase().endsWith(".zip")) {
        throw new PacoteInvalido(t("skills.publish.dropWrongType"));
      }

      aplicar(await lerZip(arquivo));
    } catch (falha) {
      setPacote(null);
      setErro(falha instanceof Error ? falha.message : t("skills.publish.readFailed"));
    } finally {
      setLendo(false);
    }
  };

  const receberSelecao = async (lista: FileList | null, comoZip: boolean) => {
    if (!lista || lista.length === 0) return;

    setLendo(true);
    setErro(null);

    try {
      aplicar(comoZip ? await lerZip(lista[0]!) : await lerSelecao(lista));
    } catch (falha) {
      setPacote(null);
      setErro(falha instanceof Error ? falha.message : t("skills.publish.readFailed"));
    } finally {
      setLendo(false);
    }
  };

  /** Os arquivos do pacote com as declarações dos passos gravadas no `SKILL.md`. */
  const comDeclaracoes = useMemo(() => {
    if (!pacote) return [];

    // Título e descrição vão junto: a versão pode ser publicada dias depois, e
    // o que a loja vai mostrar tem de estar dentro dela, não numa tela aberta.
    const declaracoes = {
      ...paraDeclaracoes(requisitos, parametros),
      // O ARQUIVO leva um texto só — o inglês, ou o que houver. Em branco, o
      // que o pacote já declara continua valendo e nada é reescrito.
      ...(paraOArquivo(tituloI18n) ? { title: paraOArquivo(tituloI18n) } : {}),
      ...(paraOArquivo(descricaoI18n) ? { description: paraOArquivo(descricaoI18n) } : {}),
    };

    return pacote.files.map((arquivo) =>
      arquivo.path === SKILL_ENTRY_FILE && arquivo.encoding === "utf8"
        ? { ...arquivo, content: writeDeclarations(arquivo.content, declaracoes) }
        : arquivo,
    );
  }, [pacote, requisitos, parametros, tituloI18n, descricaoI18n]);

  /**
   * Pede ao Magic Fill o título e a descrição de vitrine, nos dois idiomas.
   *
   * Dispara sozinho assim que o pacote é lido: é ali que o material está inteiro
   * e não há nada para a pessoa decidir ainda. Uma chamada por idioma — o
   * `Accept-Language` é o que diz em que língua responder.
   *
   * NÃO dispara quando já há texto: numa versão nova o cadastro veio do
   * catálogo, e sobrescrevê-lo apagaria o que alguém escreveu à mão antes.
   */
  const sugerirVitrine = async (pacoteLido: SkillPackage, tituloDoPacote: string | null) => {
    setPreparando(true);

    const jaEscrito = [...Object.values(tituloI18n), ...Object.values(descricaoI18n)].some(
      (texto) => texto?.trim(),
    );

    // Versão nova já chega com cadastro: nada a sugerir, e a tela segue adiante
    // pelo mesmo caminho de sempre — o `liberar` abaixo.
    if (jaEscrito) return liberar();

    const contexto = {
      name: pacoteLido.name,
      package_description: pacoteLido.description,
      package_body: pacoteLido.body.slice(0, 2000),
      title: tituloDoPacote ?? "",
      description: "",
      // As etiquetas entram na mesma ida: o modelo já está lendo o pacote, e
      // pedir departamento, categoria e função agora sai de graça.
      facets: "",
      // Ferramenta é lista aberta, e o filtro de vocabulário do serviço
      // descartaria `playwright` por não estar previsto. Vem como texto e vira
      // `tool:<nome>` aqui.
      tools: "",
    };

    setFieldLoading("title", true);
    setFieldLoading("description", true);
    setFieldLoading("facets", true);

    try {
      const respostas = await Promise.all(
        IDIOMAS.map(async (opcao) => {
          const controlador = new AbortController();
          const relogio = window.setTimeout(() => controlador.abort(), 20000);

          try {
            const resposta = await requestMagicFill({
              formType: "skill_register",
              fields: contexto,
              // O vocabulário da organização vai junto: é ele que faz o serviço
              // injetar as instruções de faceta e devolver `[{name, value}]` em
              // vez de prosa. Sem isso a sugestão de etiqueta é descartada na
              // normalização — foi o que aconteceu nas primeiras tentativas.
              metadata: { fieldMetadata: getSkillRegisterFormMetadata(facetsParaIA) },
              signal: controlador.signal,
              language: opcao.id,
            });

            return [opcao.id, resposta?.suggestedFields ?? {}] as const;
          } finally {
            window.clearTimeout(relogio);
          }
        }),
      );

      const texto = (valor: unknown) => (typeof valor === "string" ? valor.trim() : "");

      const aplicar = (campo: "title" | "description") => (atual: Record<string, string>) => {
        const proximo = { ...atual };

        for (const [idiomaSugerido, campos] of respostas) {
          const sugerido = texto(campos[campo]);
          if (sugerido && !proximo[idiomaSugerido]?.trim()) proximo[idiomaSugerido] = sugerido;
        }

        return proximo;
      };

      setTituloI18n(aplicar("title"));
      setDescricaoI18n(aplicar("description"));

      // Etiqueta NÃO tem idioma: é identificador do vocabulário da organização.
      // Vale a resposta em inglês, que é a língua do cadastro; sem ela, a
      // primeira que vier com algo aproveitável.
      const emIngles = respostas.find(([idiomaSugerido]) => idiomaSugerido === "en");
      const sugeridas = [emIngles, ...respostas]
        .map((entrada) => {
          // Classificação primeiro, ferramenta depois: quando o teto corta, o
          // que sobrevive é o que situa a skill (área, categoria, função) — e
          // não a décima biblioteca citada no meio do procedimento.
          const vistas = new Set<string>();

          return [
            ...etiquetasDaSugestao(entrada?.[1]?.facets),
            ...ferramentasDaSugestao(entrada?.[1]?.tools),
          ]
            .filter((etiqueta) => {
              const chave = `${etiqueta.name}:${etiqueta.value}`;
              if (vistas.has(chave)) return false;
              vistas.add(chave);

              return true;
            })
            .slice(0, LIMITE_DE_ETIQUETAS);
        })
        .find((lista) => lista.length > 0);

      if (sugeridas) setEtiquetas((atual) => (atual.length > 0 ? atual : sugeridas));
    } catch (falha) {
      // Silêncio PARA QUEM IMPORTA, não para quem mantém: a sugestão é
      // CONVENIÊNCIA, e modelo fora do ar, tempo estourado ou resposta torta não
      // podem prender ninguém — os campos ficam vazios e a pessoa escreve, que é
      // como era antes dela.
      //
      // Mas sem registro nenhum, uma falha sistemática do Magic Fill aqui vira
      // "a IA parou de preencher" sem rastro para investigar. O aviso vai pelo
      // logger da aplicação — não por `console` cru, que é bloqueante em
      // produção pela regra da casa.
      logger.warn("[skills] Magic Fill não respondeu; campos seguem vazios", falha);
    } finally {
      liberar();
    }
  };

  /**
   * Devolve a tela, aconteça o que acontecer.
   *
   * Um lugar só para apagar os spinners e avançar: espalhar isso pelos desfechos
   * é como se esquece um deles — e o esquecido trava o assistente no passo do
   * pacote, sem nada na tela explicando por quê.
   */
  const liberar = () => {
    setFieldLoading("title", false);
    setFieldLoading("description", false);
    setFieldLoading("facets", false);
    setPreparando(false);
    // A apresentação é o primeiro passo desde que o upload saiu da trilha.
    setPasso(0);
  };

  /**
   * O que a varinha manda como contexto.
   *
   * O pacote inteiro é o material: nome, o que ele declara e o começo do
   * procedimento. É mais do que qualquer outro formulário do produto tem para
   * oferecer — e é por isso que aqui a sugestão tende a ser boa.
   *
   * O corpo vai cortado: o `SKILL.md` de uma skill real tem páginas, e o que
   * decide título e descrição está nas primeiras linhas.
   */
  const contextoParaIA = () => ({
    name: lido?.name ?? "",
    package_description: lido?.description ?? "",
    package_body: (lido?.body ?? "").slice(0, 2000),
    title: tituloI18n[idioma] ?? "",
    description: descricaoI18n[idioma] ?? "",
  });

  const traducoesDeclaradas = useMemo(
    () => paraTraducoes(requisitos, parametros),
    [requisitos, parametros],
  );

  const publicar = async (instalandoJunto = false) => {
    // Sem nível não se importa: é ele que decide em qual loja a skill aparece.
    if (!pacote || !lido || !escopoFinal) return;

    setEnviando(true);
    try {
      const registrada = await SkillsAdminService.publish({
        folder_name: pacote.folderName,
        // O que foi declarado nos passos volta para DENTRO do `SKILL.md`: é o
        // arquivo, e não uma coluna do Prodgy, que a instalação lê — e é ele
        // que segue portátil se alguém levar o pacote para outro host.
        files: comDeclaracoes,
        publisher_scope: destino,
        install_scope: escopoFinal,
        // Implica publicar: não há como instalar o que não está no ar.
        ...(instalandoJunto && cabeInstalarJunto ? { install: true } : {}),
        // Core e a escolha de dono são do catálogo da plataforma: lá a skill
        // pode ser imposta a toda organização, e quem responde por ela é um
        // superadministrador. Nos outros destinos nenhuma das duas existe.
        ...(pessoal ? {} : { is_core: core }),
        ...(!pessoal && dono ? { owner_user_id: dono } : {}),
        // Vazio apaga a origem; é como se tira uma procedência informada errada.
        source_url: origem.trim() || null,
        facets:
          etiquetas.map((etiqueta) => `${etiqueta.name}:${etiqueta.value}`).join(" ") || undefined,
        changelog: paraOArquivo(changelogI18n) || undefined,
        ...(paraOArquivo(tituloI18n) ? { title: paraOArquivo(tituloI18n) } : {}),
        ...(paraOArquivo(descricaoI18n) ? { description: paraOArquivo(descricaoI18n) } : {}),
        // Traduções só quando existem: mapa vazio no banco diria que alguém
        // traduziu para nada.
        ...(Object.keys(semVazios(tituloI18n)).length > 0
          ? { title_i18n: semVazios(tituloI18n) }
          : {}),
        ...(Object.keys(semVazios(descricaoI18n)).length > 0
          ? { description_i18n: semVazios(descricaoI18n) }
          : {}),
        ...(Object.keys(semVazios(changelogI18n)).length > 0
          ? { changelog_i18n: semVazios(changelogI18n) }
          : {}),
        // O que é lido por pessoas nas declarações: o arquivo levou um idioma,
        // o Prodgy guarda todos.
        ...(Object.keys(traducoesDeclaradas.parameters).length > 0
          ? { parameters_i18n: traducoesDeclaradas.parameters }
          : {}),
        ...(Object.keys(traducoesDeclaradas.integrations).length > 0
          ? { integrations_i18n: traducoesDeclaradas.integrations }
          : {}),
      });

      // No espaço pessoal a importação fecha o ciclo — registra, publica e
      // instala —, e o aviso precisa dizer QUAL dos dois desfechos aconteceu.
      // Repetir "registrada" para uma skill que já está servindo, ou para uma
      // que não está servindo nada, deixaria a pessoa sem saber onde ela ficou.
      const faltando = registrada.unmet_requirements ?? [];
      const instalouJunto = instalandoJunto && cabeInstalarJunto;

      if (instalouJunto && faltando.length > 0) {
        toast({
          title: t("skills.publish.orgNotInstalledTitle"),
          description: t("skills.publish.orgNotInstalledDescription", {
            skill: lido.name,
            missing: faltando.map((item) => item.requirement).join(", "),
          }),
        });
      } else if (instalouJunto) {
        toast({
          title: t("skills.publish.orgInstalledTitle"),
          description: t("skills.publish.orgInstalledDescription", { skill: lido.name }),
        });
      } else if (pessoal && faltando.length > 0) {
        toast({
          title: t("skills.publish.personalNotInstalledTitle"),
          description: t("skills.publish.personalNotInstalledDescription", {
            skill: lido.name,
            missing: faltando.map((item) => item.requirement).join(", "),
          }),
        });
      } else if (pessoal) {
        toast({
          title: t("skills.publish.personalRegisteredTitle"),
          description: t("skills.publish.personalRegisteredDescription", { skill: lido.name }),
        });
      } else if (destino === "organization") {
        // "Registrada" mentiria: na organização não há rascunho, e a skill já
        // está na loja no instante em que esta mensagem aparece.
        toast({
          title: t("skills.publish.orgPublishedTitle"),
          description: t("skills.publish.orgPublishedDescription", { skill: lido.name }),
        });
      } else {
        // Sem rascunho: a skill está no ar no instante em que esta mensagem
        // aparece, e dizer "registrada" mandaria a pessoa procurar um segundo
        // ato que não existe mais.
        toast({
          title: t("skills.publish.orgPublishedTitle"),
          description: t("skills.publish.orgPublishedDescription", { skill: lido.name }),
        });
      }

      invalidarSkills(queryClient);
      fechar(false);
    } catch (falha) {
      toast({
        variant: "destructive",
        title: t("skills.publish.registerFailed"),
        description: falha instanceof Error ? falha.message : undefined,
      });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={fechar}>
      {/* Altura FIXA, e não `max-h`: cada passo tem um conteúdo de tamanho
          diferente, e com altura livre a janela pulava a cada avanço — o
          próprio botão de avançar mudava de lugar embaixo do cursor. */}
      <DialogContent className="flex h-[85vh] max-w-4xl flex-col">
        <DialogHeader>
          <DialogTitle>
            {skill
              ? t("skills.publish.newVersionTitle", { skill: skill.title || skill.name })
              : t("skills.publish.registerTitle")}
          </DialogTitle>
        </DialogHeader>

        {/* A identidade do que está sendo registrado, e o pacote de onde ela
            veio. Fica acima da régua porque não pertence a passo nenhum: nome e
            autor não se editam aqui, e o pacote é o material de todos os passos.

            Sem rótulos de campo: "Nome" e "Autor" diante de um slug e de um
            nome próprio não acrescentavam informação, só ruído numa linha que
            precisa caber ao lado do pacote. */}
        {!montando && lido && (
          <div className="flex items-center gap-3 rounded-lg border bg-muted/30 p-3">
            <Avatar className="h-10 w-10 shrink-0 rounded-md">
              <AvatarImage src={logo ?? undefined} alt={lido.name} className="object-contain" />
              <AvatarFallback className="rounded-md text-sm font-medium">
                {lido.name[0]?.toUpperCase()}
              </AvatarFallback>
            </Avatar>

            <div className="min-w-0 flex-1">
              <p className="truncate font-mono text-sm font-medium">{lido.name}</p>

              {/* O AUTOR, que é do pacote. Sem avatar: quem escreveu pode não
                  ter conta aqui, e o rosto de quem registra ao lado do nome de
                  outra pessoa era justamente a confusão que se desfez. Quem
                  responde pela skill no Prodgy é o dono, e ele se escolhe na
                  apresentação. */}
              <div className="mt-0.5 truncate text-xs text-muted-foreground">
                {autor ?? t("skills.publish.authorUndeclared")}
              </div>
            </div>

            {/* O pacote, que era a etapa 1 inteira, cabe aqui: de onde vieram os
                arquivos e como trocá-los. O carregamento ocupa o mesmo lugar, em
                vez de uma tela só dele. */}
            {preparando || lendo ? (
              <span className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-primary" />
                {t("skills.publish.loading")}
              </span>
            ) : (
              <div className="flex shrink-0 items-center gap-2 rounded-md border bg-background px-2.5 py-1.5">
                <FolderOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                <div className="leading-tight">
                  <p className="max-w-[160px] truncate text-xs font-medium">{pacote?.folderName}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {t("skills.publish.fileCount", { count: pacote?.files.length ?? 0 })}
                  </p>
                </div>

                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground"
                      onClick={trocarPacote}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{t("skills.publish.replace")}</TooltipContent>
                </Tooltip>
              </div>
            )}
          </div>
        )}

        {/* A mesma trilha do assistente de instalação, para quem publica
            reconhecer o caminho que a outra pessoa vai percorrer. */}
        {/* Só depois que os dados voltam: uma régua de passos sobre campos que
            ainda estão carregando convida a avançar por cima do que a IA está
            preenchendo. */}
        {passos.length > 0 && !montando && (
          <SkillInstallerProgress steps={passos} currentStep={Math.min(passo, passos.length - 1)} />
        )}

        {/* O respiro à direita e em cima/embaixo não é estética: o anel de foco
            dos campos é desenhado FORA da borda, e num container que rola ele
            era cortado — o campo em foco aparecia com o contorno comido de um
            lado. */}
        <div className="scroll-slim min-h-0 flex-1 overflow-y-auto px-1 py-1 pr-3">
          {montando && (
            /* A área de soltar ocupa o corpo inteiro: é o alvo do passo, e um
               retângulo pequeno no topo de um espaço vazio é um alvo pior do
               que o espaço que já está ali. */
            <div className="flex h-full flex-col gap-4">
              <div
                onDragOver={(evento) => {
                  evento.preventDefault();
                  setArrastando(true);
                }}
                onDragLeave={() => setArrastando(false)}
                onDrop={(evento) => {
                  evento.preventDefault();
                  setArrastando(false);
                  void receber(evento.dataTransfer);
                }}
                className={cn(
                  "flex min-h-0 flex-1 flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-colors",
                  arrastando ? "border-primary bg-primary/5" : "border-muted-foreground/25",
                )}
              >
                <input
                  ref={seletorPasta}
                  type="file"
                  hidden
                  // @ts-expect-error — atributo de navegador que o tipo do React não declara
                  webkitdirectory=""
                  directory=""
                  onChange={(evento) => void receberSelecao(evento.target.files, false)}
                />
                <input
                  ref={seletorZip}
                  type="file"
                  hidden
                  accept=".zip"
                  onChange={(evento) => void receberSelecao(evento.target.files, true)}
                />

                {lendo || preparando ? (
                  <>
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    {/* Uma linha só: ler o pacote e sugerir o cadastro são a
                        mesma espera para quem olha, e detalhar as etapas não
                        muda nada do que a pessoa pode fazer. */}
                    <p className="text-sm font-medium">{t("skills.publish.loading")}</p>
                  </>
                ) : pacote ? (
                  <>
                    <CheckCircle2 className="h-6 w-6 text-primary" />
                    {/* Numa versão nova, o pacote que aparece aqui veio do
                        catálogo: dizer de onde ele é evita a leitura de que
                        alguma coisa já foi enviada. */}
                    {skill && (
                      <p className="text-xs text-muted-foreground">
                        {t("skills.publish.currentPackage")}
                      </p>
                    )}
                    <p className="text-sm font-medium">{pacote.folderName}</p>
                    <p className="text-xs text-muted-foreground">
                      {t("skills.publish.fileCount", {
                        count: pacote.files.length,
                      })}
                    </p>
                    <Button variant="ghost" size="sm" onClick={trocarPacote}>
                      {t("skills.publish.replace")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Upload className="h-6 w-6 text-muted-foreground" />
                    <p className="text-sm">{t("skills.publish.dropHere")}</p>
                    <p className="text-xs text-muted-foreground">{t("skills.publish.dropHint")}</p>

                    {/* Dois botões, e não um clique na área: o seletor de PASTA
                        esconde arquivos, então quem quer o .zip não o acha ali. */}
                    <div className="mt-2 flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => seletorPasta.current?.click()}
                      >
                        {t("skills.publish.chooseFolder")}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => seletorZip.current?.click()}
                      >
                        {t("skills.publish.chooseZip")}
                      </Button>
                    </div>
                  </>
                )}
              </div>

              {erro && (
                <div className="flex shrink-0 items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                  <p className="text-xs text-destructive">{erro}</p>
                </div>
              )}

              {leitura && !leitura.ok && (
                <div className="shrink-0 space-y-2 rounded-md border border-destructive/40 bg-destructive/5 p-3">
                  <p className="flex items-center gap-1.5 text-sm font-medium text-destructive">
                    <AlertTriangle className="h-4 w-4" />
                    {t("skills.publish.invalidPackage")}
                  </p>
                  {/* O campo vem junto: "description acima do limite" sem dizer
                      onde manda a pessoa caçar num pacote de dezenas de arquivos. */}
                  {leitura.issues.map((falha) => (
                    <p key={`${falha.code}-${falha.field}`} className="text-xs text-destructive">
                      <span className="font-medium">{falha.field}</span> — {falha.message}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* `!montando` em todos os passos: enquanto o pacote e lido e a
              sugestao escrita, a tela e a do upload com um spinner, e nada mais.
              Sem esta guarda, assim que o arquivo era lido os campos ja
              apareciam ABAIXO da area de carregamento — meio formulario debaixo
              de um spinner, sem regua de passos e com a IA ainda escrevendo por
              cima do que a pessoa visse. */}
          {!montando && atual?.id === "overview" && comoDetalhe && lido && (
            <div className="space-y-5">
              {/* O cadastro do Prodgy ANTES do procedimento renderizado: é o
                  que a pessoa pode mexer, e o SKILL.md abaixo é a leitura que
                  justifica o ajuste. Invertido, o formulário ficava a uma
                  rolagem de distância — num pacote com texto longo, ninguém
                  descobria que ele existia. */}
              <div className="space-y-4 px-1">
                {/* Só o que o Prodgy acrescenta em volta do pacote tem idioma
                    (AC37). O `SKILL.md` abaixo continua como foi escrito — ele é
                    o produto da skill, e quem a lê no repositório a lê assim. */}
                <SeletorDeIdioma
                  valor={idioma}
                  onChange={setIdioma}
                  vazios={IDIOMAS.map((opcao) => opcao.id).filter(
                    (id) => !tituloI18n[id]?.trim() || !descricaoI18n[id]?.trim(),
                  )}
                />

                <div className="space-y-1.5">
                  <Label>
                    {t("skills.publish.titleField")}
                    <span className="ml-0.5 text-destructive">*</span>
                  </Label>
                  {/* A varinha do produto, e não um preenchimento próprio desta
                      tela: o contexto é o pacote, e o idioma é o da aba aberta —
                      a chamada manda o `Accept-Language`. */}
                  <InputWithMagicFill
                    formType="skill_register"
                    fieldKey="title"
                    getCurrentValues={contextoParaIA}
                    onMagicFillApply={(valor) =>
                      setTituloI18n({ ...tituloI18n, [idioma]: String(valor ?? "") })
                    }
                    value={tituloI18n[idioma] ?? ""}
                    onChange={(evento) =>
                      setTituloI18n({ ...tituloI18n, [idioma]: evento.target.value })
                    }
                  />
                </div>

                <div className="space-y-1.5">
                  <Label>
                    {t("skills.publish.descriptionField")}
                    <span className="ml-0.5 text-destructive">*</span>
                  </Label>
                  {/* Sem placeholder: o texto do pacote aqui parecia campo
                      preenchido, e a pessoa não sabia se estava editando ou
                      olhando. Em branco vale o do pacote, e isso a tela diz no
                      próprio seletor de idioma, não em rodapé de campo. */}
                  <TextareaWithMagicFill
                    formType="skill_register"
                    fieldKey="description"
                    getCurrentValues={contextoParaIA}
                    onMagicFillApply={(valor) =>
                      setDescricaoI18n({ ...descricaoI18n, [idioma]: String(valor ?? "") })
                    }
                    value={descricaoI18n[idioma] ?? ""}
                    onChange={(evento) =>
                      setDescricaoI18n({ ...descricaoI18n, [idioma]: evento.target.value })
                    }
                    // A descrição é o que o agente lê para escolher a skill, e
                    // costuma ter algumas linhas: com três, a pessoa revisava o
                    // texto por uma fresta.
                    rows={8}
                    maxLength={1024}
                  />
                </div>
                {/* A procedência, e logo abaixo o dono: é a mesma pergunta em
                    dois tempos — de onde isto veio, e quem responde por isto
                    aqui dentro. Vazio é legítimo e não trava o avanço; skill
                    escrita na casa não tem origem externa. */}
                <div className="space-y-1.5">
                  <Label>{t("skills.publish.sourceField")}</Label>
                  <Input
                    type="url"
                    value={origem}
                    onChange={(evento) => setOrigem(evento.target.value)}
                    placeholder={t("skills.publish.sourcePlaceholder")}
                  />
                </div>

                {/* O dono é decisão do Prodgy, não do pacote: por isso fica
                    aqui, e não no cabeçalho ao lado do autor. Sem idioma — é uma
                    pessoa, não um texto.

                    No espaço pessoal não há escolha: a dona é quem está
                    importando, e a lista é de superadministradores. */}
                {!pessoal && (
                <div className="space-y-1.5">
                  <Label>{t("skills.publish.ownerField")}</Label>
                  <Select value={dono ?? ""} onValueChange={setDono}>
                    <SelectTrigger>
                      <SelectValue placeholder={t("skills.publish.ownerPlaceholder")} />
                    </SelectTrigger>
                    <SelectContent>
                      {superadmins.map((perfil) => (
                        <SelectItem key={perfil.id} value={perfil.id}>
                          <span className="flex items-center gap-2">
                            <Avatar className="h-5 w-5">
                              <AvatarImage
                                src={
                                  (perfil.id === user?.id ? avatarUrl : perfil.avatar_url) ||
                                  undefined
                                }
                                alt={perfil.full_name ?? perfil.email}
                              />
                              <AvatarFallback className="text-[9px]">
                                {(perfil.full_name ?? perfil.email)[0]?.toUpperCase()}
                              </AvatarFallback>
                            </Avatar>
                            {perfil.full_name || perfil.email}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}

                {/* As etiquetas ficam aqui, e não na revisão: elas saem da
                    mesma sugestão da IA que escreveu o título e a descrição, e
                    conferir as três coisas no mesmo lugar é conferir UMA
                    sugestão. Sem seletor de idioma — etiqueta é identificador,
                    e traduzi-la criaria duas facets para a mesma coisa.

                    Estrutura LIVRE, com o cadastro existente só como sugestão:
                    a skill de plataforma não pertence a uma organização, e somar
                    o vocabulário de todas repetiria o mesmo par uma vez por
                    empresa. Quem importa declara; a instalação é que cria a
                    facet dentro da organização que instalou (AC27). */}
                <div className="space-y-1.5">
                  <Label>{t("skills.publish.facets")}</Label>
                  <SkillFacetsField
                    value={etiquetas}
                    onChange={setEtiquetas}
                    sugestoes={sugestoesDeEtiqueta}
                  />
                </div>
              </div>

              {/* Aqui NÃO vai o `SKILL.md` nem a licença: o passo seguinte é o
                  conteúdo do pacote inteiro, e repeti-los em sanfona nesta
                  página deixava ambíguo qual das duas leituras era a boa. Esta é
                  a página do que o Prodgy acrescenta; o pacote tem a dele. */}
            </div>
          )}

          {!montando && atual?.id === "content" && comoDetalhe && (
            <FilesStep
              detail={comoDetalhe}
              cacheKey={["publish", pacote?.folderName ?? "-"]}
              readFile={lerArquivo}
              // Agora a janela tem altura própria, então o editor herda o corpo
              // inteiro em vez de medir a viewport por conta própria.
              className="h-full"
            />
          )}

          {!montando && atual?.id === "integrations" && (
            <div className="space-y-4">
              <SeletorDeIdioma valor={idioma} onChange={setIdioma} />
              <IntegrationsDeclarationStep
                requisitos={requisitos}
                onChange={setRequisitos}
                idioma={idioma}
              />
            </div>
          )}

          {!montando && atual?.id === "parameters" && (
            <div className="space-y-4">
              <SeletorDeIdioma valor={idioma} onChange={setIdioma} />
              <ParametersDeclarationStep
                parametros={parametros}
                onChange={setParametros}
                idioma={idioma}
              />
            </div>
          )}

          {!montando && atual?.id === "catalog" && lido && (
            <div className="space-y-4">
              {/* Nível e core não existem no espaço pessoal: lá só há um
                  nível possível, e impor uma skill a toda organização é decisão
                  do catálogo da plataforma. */}
              {!pessoal && (
              <div className="space-y-1.5">
                <Label>
                  {t("skills.publish.scope")}
                  <span className="ml-0.5 text-destructive">*</span>
                </Label>
                <Select
                  value={escopo}
                  onValueChange={(valor) => setEscopo(valor as SkillInstallationScope)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("skills.publish.scopePlaceholder")} />
                  </SelectTrigger>
                  {/* Sem "só para mim": o catálogo da plataforma serve empresas
                      e times. Quem quer um procedimento só seu cria no próprio
                      perfil — não publicando para todo mundo num nível que
                      ninguém mais alcança. */}
                  <SelectContent>
                    <SelectItem value="organization">{t("skills.scope.organization")}</SelectItem>
                    <SelectItem value="product">{t("skills.scope.product")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              )}

              {/* Core é decisão de alcance, e mora ao lado do nível de
                  instalação: a skill core não se instala — ela já vale para
                  todas as organizações, e nenhuma pode recusá-la. */}
              {destino === "platform" && (
                <div className="flex items-start gap-3 rounded-md border p-4">
                  <Switch checked={core} onCheckedChange={setCore} className="mt-0.5" />
                  <Label className="text-sm">{t("skills.publish.core")}</Label>
                </div>
              )}

              {/* Changelog só a partir da segunda versão: na primeira não há o
                  que comparar, e o campo pedia à pessoa que descrevesse a
                  mudança em relação a nada. Na primeira, o passo simplesmente
                  não traz o campo — avisar que ele não existe é mais texto do
                  que ausência nenhuma. */}
              {jaNoCatalogo && (
                <div className="space-y-1.5">
                  <Label>
                    {t("skills.publish.changelogFor", {
                      version: (jaNoCatalogo.version ?? 0) + 1,
                    })}
                  </Label>
                  <SeletorDeIdioma valor={idioma} onChange={setIdioma} />
                  <Textarea
                    value={changelogI18n[idioma] ?? ""}
                    onChange={(evento) =>
                      setChangelogI18n({ ...changelogI18n, [idioma]: evento.target.value })
                    }
                    rows={2}
                    placeholder={t("skills.publish.changelogPlaceholder")}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            variant="ghost"
            onClick={() => (passo === 0 ? fechar(false) : setPasso(passo - 1))}
            disabled={enviando}
          >
            {passo === 0 ? t("common.cancel") : t("skills.steps.back")}
          </Button>

          {ultimo ? (
            /* Dois botões, e não um botão mais uma marcação: a marcação só
               revela o efeito depois que a pessoa aperta outra coisa. Aqui cada
               botão diz no rótulo o que vai acontecer, e o que não cabe naquele
               pacote simplesmente não aparece — ver `cabeInstalarJunto`. */
            <div className="flex items-center gap-2">
              <Button
                variant={cabeInstalarJunto ? "outline" : "default"}
                onClick={() => publicar(false)}
                disabled={!lido || !escopoFinal || enviando}
              >
                {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {/* Ao lado do par, "Publicar" — o contraste com "Publicar e
                    instalar" é o que informa. Sozinho, "Importar", que é o
                    nome do ato inteiro. */}
                {t(cabeInstalarJunto ? "skills.publish.registerOnly" : "skills.publish.register")}
              </Button>

              {cabeInstalarJunto && (
                <Button onClick={() => publicar(true)} disabled={!lido || enviando}>
                  {enviando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {t("skills.publish.registerAndInstall")}
                </Button>
              )}
            </div>
          ) : (
            <Button onClick={() => setPasso(passo + 1)} disabled={montando || faltaCadastro}>
              {t("skills.steps.next")}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
